import React, { Component, FormEvent, KeyboardEvent, FocusEvent, MouseEvent } from 'react';
import Adapter from './adapter';
import { DEFAULT_SUPPLIER_THUMBNAIL } from '../../helpers/suppliers';
import { Hit, AutocompleteRequest } from '../../rpc/admin/search_pb';
import { Link } from 'react-router-dom';
import { getHistory } from '../../helpers/history';

interface HeaderProps {
    adapter: Adapter;
    query: string ;
    basePath: string;
}

interface HeaderState {
    query: string;
    autocompleteRequestId: string;
    hits: Hit[];
    completions: string[];
    selectedIndex: number;
}

class Header extends Component<HeaderProps, HeaderState> {

    constructor(props: HeaderProps) {
        super(props);
        this.state = {
            query: this.props.query,
            autocompleteRequestId: '',
            hits: [],
            completions: [],
            selectedIndex: -1,
        };
    }

    componentDidUpdate(prevProps: HeaderProps) {
        if (prevProps.query !== this.props.query) {
            this.setState({
                query: this.props.query,
            });
        }
    }

    handleQueryChange = (e: FormEvent<HTMLInputElement>) => {
        const query = e.currentTarget.value;

        if (query.trim() !== '') {
            const id = Math.random().toString(36).substring(2);

            const request = new AutocompleteRequest();
            request.setId(id);
            request.setQuery(query.trim());
            request.setLimit(7);

            this.props.adapter.autocomplete(request).then((response) => {
                if (response.getId() === this.state.autocompleteRequestId) {
                    this.setState({
                        hits: response.getHitsList(),
                        completions: response.getCompletionsList(),
                        autocompleteRequestId: '',
                        selectedIndex: -1,
                    });
                }
            }).catch((err) => {
                console.error('Autocompletion failed: ' + err);
                this.setState({
                    autocompleteRequestId: '',
                    hits: [],
                    completions: [],
                    selectedIndex: -1,
                });
            });

            this.setState({
                query: query,
                autocompleteRequestId: id,
            });
        } else {
            this.setState({
                query: query,
                autocompleteRequestId: '',
                hits: [],
                completions: [],
                selectedIndex: -1,
            });
        }
    };

    handleCompletion = (e: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
        e.preventDefault();
        console.log('handleCompletion')
    }

    handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.keyCode === 27) {
            // Escape
            e.preventDefault();
            this.setState({
                autocompleteRequestId: '',
                hits: [],
                completions: [],
                selectedIndex: -1,
            });
        } else if (e.keyCode === 38) {
            // Arrow up
            if (this.state.selectedIndex > -1) {
                e.preventDefault();
                this.setState({
                    selectedIndex: this.state.selectedIndex - 1,
                });
            }
        } else if (e.keyCode === 40) {
            // Arrow down
            if (this.state.selectedIndex + 1 < this.state.hits.length + this.state.completions.length) {
                e.preventDefault();
                this.setState({
                    selectedIndex: this.state.selectedIndex + 1,
                });
            }
        }
    }

    handleBlur = (e: FocusEvent<HTMLInputElement>) => {
        if (e.relatedTarget) {
            // The input blur event closes the menu, so a click on a link in the menu is not
            // recognized. Instead, look for what blurred the input and route to the
            // link if it was a link in the dropdown.
            const o: any = e.relatedTarget;
            if (o.dataset) {
                if (o.dataset['link']) {
                    getHistory().push(o.dataset['link']);
                } else if (o.dataset['query']) {
                    this.setState({
                        query: o.dataset['query'],
                    });
                    this.props.adapter!.setSearchQuery(this.state.query);
                }
            }
        }
        this.setState({
            autocompleteRequestId: '',
            hits: [],
            completions: [],
            selectedIndex: -1,
        });
    }

    handleFormSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (this.state.selectedIndex < 0) {
            this.props.adapter!.setSearchQuery(this.state.query);
        } else {
            let index = this.state.selectedIndex;
            if (index < this.state.completions.length) {
                this.props.adapter!.setSearchQuery(this.state.completions[index]);
            } else {
                index -= this.state.completions.length;
                const link = this.props.basePath + encodeURIComponent(this.state.hits[index].getSku());
                getHistory().push(link);
            }
        }

        this.setState({
            autocompleteRequestId: '',
            hits: [],
            completions: [],
            selectedIndex: -1,
        });
    }

    render() {
        const dropdownClass = (this.state.hits.length + this.state.completions.length) ? ' is-active' : '';
        const hr = (this.state.hits.length && this.state.completions.length) ? <hr /> : <></>;
        let dropdownIndex = 0;

        return (
            <form onSubmit={this.handleFormSubmit} className={"search-header dropdown" + dropdownClass}>
                <div className="field has-addons search-field dropdown-trigger">
                    <div className="control search-field-input-control">
                        <input type="text"
                            className="input is-fullwidth"
                            value={this.state.query}
                            onChange={this.handleQueryChange}
                            onKeyDown={this.handleKeyPress}
                            onBlur={this.handleBlur}
                            placeholder="Search" />
                    </div>
                    <div className="control search-field-submit-control">
                        <button type="submit" className="button is-primary">Search</button>
                    </div>
                </div>
                <div className="dropdown-menu" id="dropdown-menu" role="menu">
                    <div className="dropdown-content">
                        {
                            this.state.completions.length ? <div className="search-suggestions">Search suggestions</div> : <></>
                        }
                        {
                            this.state.completions.map((completion) => {
                                const itemClass = (dropdownIndex++ === this.state.selectedIndex) ? ' is-active' : '';
                                return (<button className={"dropdown-item autocomplete-item" + itemClass}
                                        data-query={completion} key={'search-' + dropdownIndex}
                                        onClick={this.handleCompletion}>{completion}</button>);
                            })
                        }
                        {
                            hr
                        }
                        {
                            this.state.hits.map((hit) => {
                                const itemClass = (dropdownIndex++ === this.state.selectedIndex) ? ' is-active' : '';
                                const link = this.props.basePath + encodeURIComponent(hit.getSku());
                                const thumbnail = hit.getThumbnail() ? hit.getThumbnail() : DEFAULT_SUPPLIER_THUMBNAIL;
                                return (
                                    <Link to={link} key={'search-' + dropdownIndex} className={"dropdown-item autocomplete-item" + itemClass} data-link={link}>
                                        <div className="autocomplete-image is-32x32">
                                            <img src={thumbnail} alt={hit.getName()} width="32" height="32" />
                                        </div>
                                        <div className="autocomplete-image">{hit.getName()}</div>
                                    </Link>);
                            })
                        }
                    </div>
                </div>
            </form>
        );
    }
}

export default Header;